import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Database, ArrowRight, Beaker, MessageSquare, LayoutDashboard, Plug, Bot, TrendingUp, Presentation } from 'lucide-react';
import './HomePage.css';

const LDM_LOGO = 'https://endavant-cdns.s3.us-east-2.amazonaws.com/web-ldm/img/logo-light.svg';

// Experiments data
const experiments = [
  {
    id: 'asistente-clickup',
    title: 'Asistente ClickUp',
    description: 'Agente de IA que consulta datos de ClickUp. Pregunta por el equipo, tareas, productividad y métricas usando lenguaje natural.',
    tags: ['MCP', 'Gemini 2.5 Pro', 'Vertex AI', 'Databricks Lakebase'],
    icon: Database,
    status: 'Activo',
    route: '/chat',
    featured: true,
    gradient: 'linear-gradient(135deg, #0ea5e9 0%, #06b6d4 50%, #14b8a6 100%)',
  },
  {
    id: 'asistente-netsuite',
    title: 'Asistente NetSuite',
    description: 'Agente de IA que consulta datos de NetSuite. Disponible directamente en Google Chat como NetSuiteBot.',
    tags: ['Databricks Lakehouse', 'Genie', 'Google AppScripts'],
    icon: MessageSquare,
    status: 'Activo',
    route: '/netsuite',
    featured: false,
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 50%, #6d28d9 100%)',
  },
  {
    id: 'asistente-marketing',
    title: 'Asistente de Marketing',
    description: 'Agente de IA experto en publicidad digital. Analiza métricas de campañas, conversiones, keywords y rendimiento con datos de Lakebase.',
    tags: ['Lakebase', 'Gemini 2.5 Pro', 'Vertex AI', 'Ads Analytics'],
    icon: TrendingUp,
    status: 'Activo',
    route: '/marketing',
    featured: false,
    gradient: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 50%, #4338ca 100%)',
  },
  {
    id: 'dashboard-builder',
    title: 'Dashboard Builder',
    description: 'Construye dashboards conversacionalmente. Pide gráficos, tablas y KPIs con lenguaje natural y observa cómo se generan.',
    tags: ['MCP', 'Gemini 2.5 Pro', 'Vertex AI', 'Recharts'],
    icon: LayoutDashboard,
    status: 'Activo',
    route: '/dashboard',
    featured: false,
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #f97316 50%, #ef4444 100%)',
  },
  {
    id: 'deck-builder',
    title: 'Deck Builder',
    description: 'Genera presentaciones profesionales automáticamente. Sube un archivo de texto y obtén un deck listo en Gamma.',
    tags: ['Gamma API', 'AI Presentations', 'TXT/MD'],
    icon: Presentation,
    status: 'Activo',
    route: '/deck-builder',
    featured: false,
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 50%, #6d28d9 100%)',
  },
  {
    id: 'addon-workspace',
    title: 'Addon Workspace (Datahub)',
    description: 'Addon de Google Workspace que conecta Databricks Lakehouse directamente con Google Slides y Sheets. Jala datos en tiempo real y crea presentaciones automáticas.',
    tags: ['Databricks', 'Google Workspace', 'Apps Script', 'Genie'],
    icon: Plug,
    status: 'Próximamente',
    route: null,
    featured: false,
    gradient: 'linear-gradient(135deg, #22c55e 0%, #16a34a 50%, #15803d 100%)',
  },
  {
    id: 'skynet-dev',
    title: 'Skynet Dev',
    description: 'Experimento de desarrollo autónomo: Genie (con conocimiento de datos) y Claude (experto en código) colaboran para desarrollar soluciones con mínima intervención humana.',
    tags: ['Genie', 'Claude Sonnet', 'Agentic', 'Full AI Control'],
    icon: Bot,
    status: 'Próximamente',
    route: null,
    featured: true, // Destacado especial
    special: 'skynet', // Clase especial para estilo único
    gradient: 'linear-gradient(135deg, #18181b 0%, #27272a 50%, #3f3f46 100%)',
    // Logos de las AI que colaboran (fondo negro perfecto para tema dark)
    aiLogos: [
      '/logos/databricks-logo.png',
      '/logos/claude-logo.jpg'
    ]
  },
];

export default function HomePage() {
  const navigate = useNavigate();

  const handleExperimentClick = (experiment) => {
    if (experiment.route) {
      navigate(experiment.route);
    }
  };

  return (
    <div className="labs-page">
      {/* Subtle grid background */}
      <div className="labs-bg-grid" />

      {/* Header */}
      <header className="labs-header">
        <div className="labs-header-inner">
          <div className="labs-logo-group" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img src={LDM_LOGO} alt="LDM" className="labs-logo-img" />
            <span className="labs-logo-text">labs</span>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="labs-hero">
        <motion.div
          className="labs-hero-content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <div className="labs-hero-badge">
            <Beaker size={14} />
            <span>Prototipos y experimentos</span>
          </div>
          <h1 className="labs-hero-title">
            El hogar de los<br />
            <span className="labs-hero-gradient-text">experimentos de AI</span>
          </h1>
          <p className="labs-hero-subtitle">
            Exploramos el futuro del marketing digital con inteligencia artificial.
            Herramientas experimentales creadas por el equipo de innovación de LDM.
          </p>
        </motion.div>
      </section>

      {/* All Experiments */}
      <section className="labs-section">
        <div className="labs-section-inner">
          <div className="labs-section-header">
            <Beaker size={18} className="labs-section-icon" />
            <h2 className="labs-section-title">Todos los experimentos</h2>
          </div>

          <div className="labs-grid">
            {experiments.map((exp, index) => {
              const Icon = exp.icon;
              const isActive = !!exp.route;
              const isSkynet = exp.special === 'skynet';
              
              return (
                <motion.div
                  key={exp.id}
                  className={`labs-card ${!isActive ? 'labs-card-disabled' : ''} ${isSkynet ? 'labs-card-skynet' : ''}`}
                  data-testid="experiment-card"
                  onClick={() => isActive && handleExperimentClick(exp)}
                  whileHover={isActive ? { y: -4, transition: { duration: 0.2 } } : {}}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 * (index + 1) }}
                >
                  <div className="labs-card-header">
                    {isSkynet && exp.aiLogos ? (
                      <div className="labs-card-ai-collab">
                        <div className="labs-ai-logo-wrapper">
                          <img src={exp.aiLogos[0]} alt="Databricks" className="labs-ai-logo" />
                        </div>
                        <div className="labs-ai-connector">×</div>
                        <div className="labs-ai-logo-wrapper">
                          <img src={exp.aiLogos[1]} alt="Claude" className="labs-ai-logo" />
                        </div>
                      </div>
                    ) : (
                      <div className="labs-card-icon" style={{ background: exp.gradient }}>
                        <Icon size={20} color="white" />
                      </div>
                    )}
                    <span className={`labs-card-status ${isActive ? 'active' : 'soon'} ${isSkynet ? 'skynet' : ''}`}>
                      <span className={`labs-status-dot ${isActive ? 'active' : 'soon'}`} />
                      {exp.status}
                    </span>
                  </div>
                  <h3 className={`labs-card-title ${isSkynet ? 'skynet' : ''}`}>{exp.title}</h3>
                  <p className={`labs-card-desc ${isSkynet ? 'skynet' : ''}`}>{exp.description}</p>
                  <div className="labs-tags">
                    {exp.tags.map((tag, i) => (
                      <span key={i} className={`labs-tag ${isSkynet ? 'skynet' : ''}`}>{tag}</span>
                    ))}
                  </div>
                  {isActive && (
                    <div className="labs-card-action">
                      <span className="labs-open-link">
                        Abrir <ArrowRight size={14} />
                      </span>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="labs-footer">
        <div className="labs-footer-inner">
          <img src={LDM_LOGO} alt="LDM" className="labs-footer-logo" />
          <span className="labs-footer-labs">labs</span>
          <p className="labs-footer-text">
            © {new Date().getFullYear()} LDM · Forward Thinking Marketing
          </p>
        </div>
      </footer>
    </div>
  );
}
